import React from 'react';
import parse from 'html-react-parser';

import Button from '../../Shared/UI/Button/components/Button';
import DeleteIcon from '../../Shared/UI/DeleteIcon/components/DeleteIcon';

import EditableCaption from './EditableCaption';

import {
  handleSelectionChange,
  updateFromKeydown,
  deleteImageComponent,
  handlePaste,
} from '../dom/RichTextEditorHelpers';

const RichTextEditorContent = ({
  setState,
  state,
  contentEditable,
  classNames = {},
}) => {
  const parseDocumentComponent = (documentComponent, documentComponentId) => {
    const htmlTextFromDocumentComponent = (documentComponent) => {
      const content = documentComponent.unstyledContent;
      const styling = documentComponent.styling;
      if (!styling || !content) return '';

      const sorted = [...styling].sort((a, b) => a.start - b.start);
      const segments = [];
      let pos = 0;

      for (const seg of sorted) {
        const segStart = Math.max(seg.start, 0);
        const segEnd = Math.min(seg.end, content.length - 1);
        if (segStart > segEnd) continue;
        if (segStart > pos) {
          segments.push({ start: pos, end: segStart - 1, types: ['none'] });
        }
        segments.push(seg);
        if (segEnd >= pos) pos = segEnd + 1;
      }
      if (pos < content.length) {
        segments.push({ start: pos, end: content.length - 1, types: ['none'] });
      }

      return segments.map((style, index) => {
        const stylingPrefix = style.types.map((type) => {
          switch (type) {
            case 'i':
              return `<i id=\"${documentComponentId}-${index}\">`;
            case 'b':
              return `<b id=\"${documentComponentId}-${index}\">`;
            case 'sup':
              return `<sup id=\"${documentComponentId}-${index}\">`;
            case 'a':
              return `<a id=\"${documentComponentId}-${index}\" href=\"${style.href}\">`;
            default:
              return `<span id=\"${documentComponentId}-${index}\">`;
          }
        }).join("");

        const textContent = documentComponent.unstyledContent.substring(style.start, style.end + 1);

        const stylingPostfix = style.types.map((type) => {
          switch (type) {
            case 'i':
              return '</i>';
            case 'b':
              return '</b>';
            case 'sup':
              return '</sup>';
            case 'a':
              return '</a>';
            default:
              return '</span>';
          }
        }).join("");

        return stylingPrefix + textContent + stylingPostfix;
      }).join("");
    };

    const htmlText = htmlTextFromDocumentComponent(documentComponent);

    return parse(htmlText, {
      replace: (domNode) => {
        if (domNode.name === 'a' && domNode.attribs) {
          const finalHref = domNode.attribs.href?.includes('http') ? domNode.attribs.href : `//${domNode.attribs.href}`;
          return <a href={finalHref} target="_blank" rel="noopener noreferrer">{domNode.children[0]?.data}</a>;
        }
      }
    });
  };

  const htmlForDocumentComponent = (documentComponent) => {
    switch(documentComponent.documentComponentType) {
      case 'DocumentTitle':
        return <h1 id={documentComponent.id} className={classNames.title}>{parseDocumentComponent(documentComponent, documentComponent.id)}</h1>;
      case 'DocumentSubtitle':
        return <p id={documentComponent.id} className={classNames.subtitle}>{parseDocumentComponent(documentComponent, documentComponent.id)}</p>;
      case 'DocumentSectionTitle':
        if (documentComponent.size == 2) {
          return <h2 id={documentComponent.id} className={classNames.sectionTitle} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</h2>;
        }

        return <h3 id={documentComponent.id} className={classNames.sectionTitle} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</h3>;
      case 'DocumentImage':
        const imageSectionVariant = {
          'large': classNames.imageSection,
          'medium': classNames.imageSection,
          'small': classNames.imageSectionSmall,
        }[documentComponent?.displaySizeVariant] || classNames.imageSection;

        const imageSrc = documentComponent?.pendingImageData || documentComponent?.imagePath;

        return (
          <div id={imageSectionVariant} className={classNames.imageContainer}>
            <img id={documentComponent.id} className={classNames.image} src={imageSrc} alt={documentComponent?.altText || ''} />
            <EditableCaption
              componentId={documentComponent.id}
              caption={documentComponent?.caption}
              contentEditable={contentEditable}
              setState={setState}
              classNames={classNames}
            />
            {contentEditable && (
              <div className={classNames.imageDeleteOverlay}>
                <Button
                  variant="destructive"
                  icon={<DeleteIcon />}
                  iconOnly
                  aria-label="Delete Image"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteImageComponent({ componentId: documentComponent.id, setState });
                  }}
                />
              </div>
            )}
          </div>
        );
      case 'DocumentParagraph':
        return <p id={documentComponent.id} className={classNames.paragraph} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</p>;
      case 'DocumentEmphasis':
        return <p id={documentComponent.id} className={classNames.emphasis} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</p>;
      case 'DocumentCitationsSection':
        const citations = Object.values(documentComponent.documentCitations)
          .sort((thisComponent, thatComponent) => {
            return thisComponent.ordinal - thatComponent.ordinal;
          })
          ?.map((citation) => {
            const fullCitationId = `${documentComponent.id}-${citation.id}`;
            return <li id={fullCitationId} className={classNames.citation} key={citation.id}>{parseDocumentComponent(citation, fullCitationId)}</li>;
          });

        return <ol id={documentComponent.id}>{citations}</ol>;
      case 'DocumentUnorderedList':
        const listItems = Object.values(documentComponent.documentUnorderedListItems)
          .sort((thisComponent, thatComponent) => {
            return thisComponent.ordinal - thatComponent.ordinal;
          })
          ?.map((item) => {
            const fullItemId = `${documentComponent.id}-${item.id}`;
            return <li id={fullItemId} className={classNames.unorderedListItem} key={item.id}>{parseDocumentComponent(item, fullItemId)}</li>;
          });

        return <ul id={documentComponent.id} className={classNames.unorderedList}>{listItems}</ul>;
      case 'DocumentResumeContactHeader':
        const headerChildren = Object.values(documentComponent.contactHeaderChildren)
          ?.map((child) => {
            const fullChildId = `${documentComponent.id}-${child.id}`;
            return <div id={fullChildId} className={classNames[`resumeContactHeader_${child.documentComponentType}`]} key={child.id}>{parseDocumentComponent(child, fullChildId)}</div>;
          });

        return <div id={documentComponent.id} className={classNames.resumeContactHeader}>{headerChildren}</div>;
      case 'DocumentResumeEntryDateRange':
        return <p id={documentComponent.id} className={classNames.resumeEntryDateRange} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</p>;
      case 'DocumentResumeEntryTitle':
        return <p id={documentComponent.id} className={classNames.resumeEntryTitle} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</p>;
      default:
        return null;
    }
  };

  const sortedDocumentComponents = Object.values(state?.documentManager?.getCurrentDocument()?.documentComponents).sort((thisComponent, thatComponent) => {
    return thisComponent.ordinal - thatComponent.ordinal;
  });

  return (
    <>
      <div
        id={classNames.container}
        contentEditable={contentEditable}
        suppressContentEditableWarning={true}
        onKeyDown={(event) => updateFromKeydown({ event, setState })}
        onBeforeInput={(event) => event.preventDefault()}
        onClick={() => handleSelectionChange({ editableContentId: classNames.container, setState })}
        onFocus={() => handleSelectionChange({ editableContentId: classNames.container, setState })}
        onPaste={(event) => handlePaste({ event, setState })}
      >
        {sortedDocumentComponents.map((documentComponent) => {
          return htmlForDocumentComponent(documentComponent);
        })}
      </div>
    </>
  );
};

export default RichTextEditorContent;
